import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS } from './routes/ids.js';
import { FACT_CGT, FACT_REALIZED, FACT_VALUATION, REPORT_EXPORT, REPORTS_VIEW, } from '../operations/index.js';
const tk = (value) => value;
const qdi = (value) => value;
const fid = (value) => value;
const formId = (value) => value;
const operation = (value) => value;
const telemetry = (value) => value;
export const PMS_ANALYTICAL_REPORT_IDS = Object.freeze({
    REALIZED_GAIN_LOSS: 'report.portfolio.realized-gain-loss',
    UNREALIZED_GAIN_LOSS: 'report.portfolio.unrealized-gain-loss',
    ANALYTICAL_CGT: 'report.tax.cgt-analytics',
});
function metadata(id, accessibilityLabelKey, operationId = REPORTS_VIEW) {
    return {
        id,
        schemaVersion: 1,
        module: MODULE_KEYS.REPORTS,
        feature: FEATURE_KEYS.REPORTS_VIEW,
        operationId: operation(operationId),
        privacyClassification: 'FINANCIAL',
        telemetryId: telemetry(`experience.${id}`),
        accessibilityLabelKey: tk(accessibilityLabelKey),
        availability: 'AVAILABLE',
        owner: 'investment-reporting-ux',
    };
}
function field(id, name, labelKey, fieldType, overrides = {}) {
    return {
        ...metadata(id, labelKey),
        id: fid(id),
        name,
        labelKey: tk(labelKey),
        fieldType,
        value: { submitBehavior: 'include', hiddenValuePolicy: 'unregister' },
        layout: { xs: 12, sm: 6, md: 4 },
        ...overrides,
    };
}
const CLIENT_FIELD = field('field.reports.pms.clientAccountId', 'clientAccountId', 'reports.liveParameters.clientAccount', 'select', {
    optionSource: {
        adapterId: 'api',
        operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST,
        valuePath: 'id',
        labelPath: 'fullName',
        allowSearch: true,
        minimumSearchLength: 0,
    },
});
const PORTFOLIO_FIELD = field('field.reports.pms.portfolioAccountId', 'portfolioAccountId', 'reports.liveParameters.portfolioAccount', 'select', {
    optionSource: {
        adapterId: 'api',
        operationId: OPERATION_IDS.ORGANIZATION_PORTFOLIOS_LIST,
        valuePath: 'id',
        labelPath: 'accountNumber',
        allowSearch: true,
        minimumSearchLength: 0,
    },
});
const INSTRUMENT_FIELD = field('field.reports.pms.instrumentSymbol', 'instrumentSymbol', 'reports.liveParameters.instrument', 'text');
function liveRunAction(id) {
    return {
        id: `action.${id}.run`,
        schemaVersion: 1,
        module: MODULE_KEYS.REPORTS,
        feature: FEATURE_KEYS.REPORTS_VIEW,
        privacyClassification: 'FINANCIAL',
        telemetryId: telemetry(`experience.action.${id}.run`),
        accessibilityLabelKey: tk('reports.liveParameters.run'),
        availability: 'AVAILABLE',
        owner: 'experience-registry',
        labelKey: tk('reports.liveParameters.run'),
        iconKey: 'play',
        kind: 'command',
        commandId: `reports.live.run.${id}`,
        method: 'GET',
        intent: 'primary',
        idempotencyRequired: false,
        submitBehavior: 'form_submit',
        submitConfig: {
            validatesForm: true,
            requiresDirty: false,
            requiresValid: true,
            resetBehavior: 'none',
        },
        successBehavior: 'NO_OP',
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('reports.liveParameters.requestFailed'),
        order: 10,
        group: 'primary',
    };
}
function parameterForm(id, fields) {
    return {
        ...metadata(id, 'a11y.form.reports.parameters'),
        id: formId(id),
        titleKey: tk('reports.liveParameters.title'),
        descriptionKey: tk('reports.liveParameters.description'),
        mode: 'create',
        fields,
        actions: [liveRunAction(id)],
        submission: {
            defaultCommandId: `reports.live.run.${id}`,
            includeFields: fields.map((item) => item.id),
        },
    };
}
export const REALIZED_GAIN_LOSS_REPORT_FORM = parameterForm('form.reports.realizedGainLossParameters', [
    field('field.reports.realized.from', 'from', 'reports.liveParameters.from', 'date'),
    field('field.reports.realized.to', 'to', 'reports.liveParameters.to', 'date'),
    CLIENT_FIELD,
    PORTFOLIO_FIELD,
    INSTRUMENT_FIELD,
]);
export const UNREALIZED_GAIN_LOSS_REPORT_FORM = parameterForm('form.reports.unrealizedGainLossParameters', [
    field('field.reports.unrealized.asOfDate', 'asOfDate', 'reports.liveParameters.asOfDate', 'date'),
    CLIENT_FIELD,
    PORTFOLIO_FIELD,
    INSTRUMENT_FIELD,
]);
export const ANALYTICAL_CGT_REPORT_FORM = parameterForm('form.reports.analyticalCgtParameters', [
    field('field.reports.cgt.from', 'from', 'reports.liveParameters.from', 'date'),
    field('field.reports.cgt.to', 'to', 'reports.liveParameters.to', 'date'),
    CLIENT_FIELD,
    PORTFOLIO_FIELD,
    INSTRUMENT_FIELD,
]);
function column(id, fieldName, labelKey, fieldType) {
    return {
        ...metadata(id, labelKey),
        labelKey: tk(labelKey),
        field: fieldName,
        fieldType,
        defaultVisible: true,
        hideable: true,
        sortable: false,
        filterable: false,
        searchable: false,
        groupable: false,
        requiredVisible: false,
        resizable: true,
        reorderable: true,
        pinnable: false,
        defaultWidth: fieldType === 'money' ? 150 : 140,
        minWidth: 90,
        maxWidth: 320,
        align: fieldType === 'money' || fieldType === 'number' || fieldType === 'percentage'
            ? 'right'
            : 'left',
        nullDisplayBehavior: 'dash',
    };
}
const REALIZED_COLUMNS = [
    column('column.report.realized.saleDate', 'saleDate', 'reports.columns.saleDate', 'date'),
    column('column.report.realized.instrument', 'instrumentSymbol', 'reports.columns.instrument', 'text'),
    column('column.report.realized.portfolio', 'portfolioAccountId', 'reports.columns.portfolioAccount', 'reference'),
    column('column.report.realized.quantity', 'quantity', 'reports.columns.quantity', 'quantity'),
    column('column.report.realized.proceeds', 'saleProceeds', 'reports.columns.saleProceeds', 'money'),
    column('column.report.realized.charges', 'deductibleCharges', 'reports.columns.deductibleCharges', 'money'),
    column('column.report.realized.costBasis', 'costBasis', 'reports.columns.costBasis', 'money'),
    column('column.report.realized.gainLoss', 'realizedGainLoss', 'reports.columns.realizedGainLoss', 'money'),
    column('column.report.realized.taxableGain', 'taxableGain', 'reports.columns.taxableGain', 'money'),
    column('column.report.realized.cgt', 'capitalGainTax', 'reports.columns.capitalGainTax', 'money'),
    column('column.report.realized.holdingPeriod', 'holdingPeriodDays', 'reports.columns.holdingPeriodDays', 'number'),
];
const UNREALIZED_COLUMNS = [
    column('column.report.unrealized.asOfDate', 'asOfDate', 'reports.columns.asOfDate', 'date'),
    column('column.report.unrealized.instrument', 'instrumentSymbol', 'reports.columns.instrument', 'text'),
    column('column.report.unrealized.portfolio', 'portfolioAccountId', 'reports.columns.portfolioAccount', 'reference'),
    column('column.report.unrealized.totalCost', 'totalCost', 'reports.columns.totalCost', 'money'),
    column('column.report.unrealized.marketPrice', 'marketPrice', 'reports.columns.marketPrice', 'money'),
    column('column.report.unrealized.marketValue', 'marketValue', 'reports.columns.marketValue', 'money'),
    column('column.report.unrealized.gainLoss', 'unrealizedGainLoss', 'reports.columns.unrealizedGainLoss', 'money'),
];
const CGT_COLUMNS = [
    column('column.report.cgt.saleDate', 'saleDate', 'reports.columns.saleDate', 'date'),
    column('column.report.cgt.instrument', 'instrumentSymbol', 'reports.columns.instrument', 'text'),
    column('column.report.cgt.portfolio', 'portfolioAccountId', 'reports.columns.portfolioAccount', 'reference'),
    column('column.report.cgt.quantity', 'quantity', 'reports.columns.quantity', 'quantity'),
    column('column.report.cgt.taxableGain', 'taxableGain', 'reports.columns.taxableGain', 'money'),
    column('column.report.cgt.rate', 'cgtRate', 'reports.columns.cgtRate', 'percentage'),
    column('column.report.cgt.tax', 'capitalGainTax', 'reports.columns.capitalGainTax', 'money'),
    column('column.report.cgt.holdingPeriod', 'holdingPeriodDays', 'reports.columns.holdingPeriodDays', 'number'),
    column('column.report.cgt.ruleVersion', 'taxRuleVersion', 'reports.columns.taxRuleVersion', 'number'),
    column('column.report.cgt.ruleReference', 'taxRuleSourceReference', 'reports.columns.taxRuleSourceReference', 'text'),
];
function report(options) {
    return {
        ...metadata(options.id, options.titleKey, options.operationId),
        reportType: options.reportType,
        titleKey: tk(options.titleKey),
        descriptionKey: tk(options.descriptionKey),
        category: options.category,
        scope: 'BRANCH',
        routeId: 'reports',
        dataSourceId: qdi(`query.reports.${options.reportType.toLowerCase()}`),
        parameterFormId: String(options.form.id),
        runFormId: String(options.form.id),
        rowIdField: options.rowIdField,
        columns: options.columns,
        formats: ['json'],
        allowedFormats: ['json'],
        generatorId: `live.generator.${options.reportType.toLowerCase()}`,
        executionMode: 'LIVE_QUERY',
        operationBindings: {
            listOperationId: options.operationId,
            runOperationId: options.operationId,
            exportOperationId: REPORT_EXPORT,
        },
        lineage: {
            sourceSystem: 'finance-facts',
            sourceEntities: options.sourceEntities,
            calculationVersion: options.calculationVersion,
        },
        implemented: true,
        auditPolicy: 'ALWAYS',
    };
}
export const PMS_ANALYTICAL_REPORT_REGISTRY = Object.freeze({
    [PMS_ANALYTICAL_REPORT_IDS.REALIZED_GAIN_LOSS]: report({
        id: PMS_ANALYTICAL_REPORT_IDS.REALIZED_GAIN_LOSS,
        reportType: 'REALIZED_GAIN_LOSS',
        titleKey: 'reports.types.realizedGainLoss',
        descriptionKey: 'reports.descriptions.realizedGainLoss',
        category: 'PORTFOLIO',
        operationId: FACT_REALIZED,
        form: REALIZED_GAIN_LOSS_REPORT_FORM,
        columns: REALIZED_COLUMNS,
        rowIdField: 'id',
        sourceEntities: ['RealizedGainLossFact', 'CGTFact'],
        calculationVersion: 'realized-gain-loss-facts-v1',
    }),
    [PMS_ANALYTICAL_REPORT_IDS.UNREALIZED_GAIN_LOSS]: report({
        id: PMS_ANALYTICAL_REPORT_IDS.UNREALIZED_GAIN_LOSS,
        reportType: 'UNREALIZED_GAIN_LOSS',
        titleKey: 'reports.types.unrealizedGainLoss',
        descriptionKey: 'reports.descriptions.unrealizedGainLoss',
        category: 'PORTFOLIO',
        operationId: FACT_VALUATION,
        form: UNREALIZED_GAIN_LOSS_REPORT_FORM,
        columns: UNREALIZED_COLUMNS,
        rowIdField: 'id',
        sourceEntities: ['PositionFact', 'ValuationFact'],
        calculationVersion: 'latest-active-position-valuation-v1',
    }),
    [PMS_ANALYTICAL_REPORT_IDS.ANALYTICAL_CGT]: report({
        id: PMS_ANALYTICAL_REPORT_IDS.ANALYTICAL_CGT,
        reportType: 'ANALYTICAL_CGT',
        titleKey: 'reports.types.analyticalCgt',
        descriptionKey: 'reports.descriptions.analyticalCgt',
        category: 'TAX',
        operationId: FACT_CGT,
        form: ANALYTICAL_CGT_REPORT_FORM,
        columns: CGT_COLUMNS,
        rowIdField: 'id',
        sourceEntities: ['CGTFact'],
        calculationVersion: 'cgt-facts-v1',
    }),
});
export const PMS_ANALYTICAL_REPORT_FORMS = Object.freeze({
    [REALIZED_GAIN_LOSS_REPORT_FORM.id]: REALIZED_GAIN_LOSS_REPORT_FORM,
    [UNREALIZED_GAIN_LOSS_REPORT_FORM.id]: UNREALIZED_GAIN_LOSS_REPORT_FORM,
    [ANALYTICAL_CGT_REPORT_FORM.id]: ANALYTICAL_CGT_REPORT_FORM,
});
